import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { concat, find } from 'lodash'

import { sectionsMap } from '@/components/sections'
import Filters, { FiltersProps } from '@/components/sections/Filters'
import LoadMore from '@/components/sections/LoadMore'
import Spacer from '@/components/sections/Spacer'
import { normalizePostsCollection } from '@/utils/pageData'
import { getPromiseFromFetch } from '@/utils/request'
import { API } from '@/constants'

import { useStore } from '@/contexts/store'
import { Section, WPHeaders, WPTaxonomy } from '@/types'

type ArchivePostTypesProps = Section &
  Partial<{
    post_type: string
    component_type: string
    collection: any[]
    filter: string
    headers: WPHeaders
    per_page: string
    taxonomy: string
    taxonomy_term: string
    filter_terms: WPTaxonomy[]
    use_pagination: boolean
    filters: FiltersProps
  }>

const ArchivePostTypes = ({
  post_type,
  component_type,
  collection: posts,
  className,
  filters,
  filter_terms,
  taxonomy,
  taxonomy_term,
  headers: postHeaders,
  use_pagination,
  per_page
}: ArchivePostTypesProps) => {
  const [headers, setHeaders] = useState<WPHeaders>(postHeaders!)
  const [currentFilter, setCurrentFilter] = useState<WPTaxonomy>()
  const [collection, setCollection] = useState<any[]>(posts as any[])
  const [pagination, setPagination] = useState(-1)
  const [fetching, setFetching] = useState(false)
  const [needsPagination, setNeedsPagination] = useState(
    headers['X-WP-TotalPages'] > 1
  )
  const previousFilter = useRef<WPTaxonomy>()
  const { strings } = useStore()

  const Section = useMemo(
    () => (component_type ? sectionsMap[component_type] : null),
    [component_type]
  )

  const getParams = useCallback(
    (page: number, filter?: WPTaxonomy) => {
      const perPage = use_pagination ? per_page : 1000
      const params = {
        per_page: perPage,
        page
      } as any

      if (filter) {
        post_type === 'post'
          ? (params['categories'] = filter?.term_id || '')
          : (params[`filter[${filter?.taxonomy || ''}]`] = filter?.slug || '')
      } else if (taxonomy_term) {
        params[`filter[${taxonomy || ''}]`] = taxonomy_term || ''
      }

      return {
        params
      }
    },
    [post_type, taxonomy, taxonomy_term, use_pagination, per_page]
  )

  const getPosts = useCallback(
    async (page: number, filter?: WPTaxonomy) => {
      const postType = post_type == 'post' ? 'posts' : post_type
      const needsReset = filter?.slug !== previousFilter.current?.slug

      setFetching(true)

      const { data, headers } = await getPromiseFromFetch({
        method: 'get',
        url: `${API.BASE}/${postType}`,
        params: getParams(page, filter)
      })

      setHeaders({
        'X-WP-Total': parseInt(headers['x-wp-total']),
        'X-WP-TotalPages': parseInt(headers['x-wp-totalpages'])
      })

      setCollection((previousCollection) => {
        const collection = normalizePostsCollection(data, strings)
        return needsReset ? collection : concat(previousCollection, collection)
      })

      setNeedsPagination(page < parseInt(headers['x-wp-totalpages']))

      setFetching(false)
    },
    [post_type, strings, getParams]
  )

  useEffect(() => {
    if (pagination < 0) return
    getPosts(pagination, currentFilter)
  }, [pagination, currentFilter, getPosts])

  return (
    <>
      {filters ? (
        <>
          <Filters
            fetching={fetching}
            className={className}
            onFilterChange={(event) => {
              setPagination(1)
              setCurrentFilter((filter) => {
                previousFilter.current = filter
                return find(filter_terms, {
                  term_id: parseInt(event?.target.value)
                })
              })
            }}
            {...filters}
          />
          <Spacer variant="sm" />
        </>
      ) : null}

      <Section
        post_type={post_type}
        collection={collection}
        className={className}
      />

      {use_pagination && needsPagination ? (
        <>
          <Spacer variant="sm" />
          <LoadMore
            className={className}
            fetching={fetching}
            onClick={() => {
              setPagination((pagination) => {
                previousFilter.current = currentFilter
                return pagination < 0 ? 2 : pagination + 1
              })
            }}
          />
        </>
      ) : null}
    </>
  )
}

export default ArchivePostTypes
